import STATUS, { createState } from 'store/states';
import { store } from 'store';
import { TASK_KEYWORDS, MATERIAL_SERIAL_NUMBER_LENGTH, ROOM_TYPES, ACT_NATIONAL_TIMING_NAMES } from 'shared/constants';
import { isEmpty, orderBy } from 'lodash';
import moment from 'moment';
import i18n from '../i18n';

export function dispatchPromise(dispatch, asyncFn, actionCreator, payload) {
  let state = createState(STATUS.loading);
  dispatch(actionCreator(state));
  return asyncFn()
    .then((response) => {
      state = createState(STATUS.resolved, response, payload);
      dispatch(actionCreator(state));
      return response;
    })
    .catch((error) => {
      state = createState(STATUS.rejected, error, payload);
      dispatch(actionCreator(state));
      return error;
    });
}

export function createDispatchFunction(asyncFn, actionCreator, payload) {
  return function (dispatch) {
    return dispatchPromise(dispatch, asyncFn, actionCreator, payload);
  };
}

export function randomFromInterval(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export function parseQueryString(queryString) {
  const query = queryString.substring(1);
  const queries = query.split('&');
  const params = {};
  queries.forEach((q) => {
    const [key, value] = q.split('=');
    params[key] = value ? decodeURIComponent(value) : value;
  });
  return params;
}

export function toQueryString(obj) {
  const params = Object.keys(obj).map(
    (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`
  );
  return params.length ? params.join('&') : '';
}

export function convertKeywords(text) {
  if (text) {
    for (let i = 0; i < TASK_KEYWORDS.length; i++) {
      let regex = new RegExp(TASK_KEYWORDS[i].key, 'g');
      text = text.replace(regex, TASK_KEYWORDS[i].value);
    }
    text = text.replace(/(?:\r\n|\r|\n)/g, '<br>');
  }

  return text;
}

export function newline2br(text) {
  return text?.replace(/(?:\r\n|\r|\n)/g, '<br>');
}

export function addLeadingZeros(num, size = MATERIAL_SERIAL_NUMBER_LENGTH) {
  let s = num + '';
  if (s) {
    while (s.length < size) s = '0' + s;
  }
  return s;
}

export const getLocationEventCapacityByTiming = (location, timingType) => {
  let sum = 0;
  if (location.locationEvent && location.locationEvent.locationEventCapacities.length) {
    location.locationEvent.locationEventCapacities.forEach(
      (exam) => (sum += exam.capacityType.timing === timingType ? exam.capacity : 0)
    );
  }
  return sum;
};

const getLocationEventCapacityByName = (location, capacityCategory, capacityType) => {
  let sum = 0;
  if (location.locationEvent && location.locationEvent.locationEventCapacities.length) {
    location.locationEvent.locationEventCapacities.forEach(
      (exam) => (sum += exam.capacityType.name === capacityType ? exam[capacityCategory] : 0)
    );
  }
  return sum;
};

export function capacityDisplayLogic(location, type, returnValues = false) {
  const assigned = getLocationEventCapacityByName(location, 'registrationAssignCount', type.name);
  const capacity = getLocationEventCapacityByName(location, 'capacity', type.name);

  if (returnValues) {
    return {
      assigned,
      capacity: type.unlimitedCapacityFlag ? null : capacity,
    };
  }

  if (type.hideIfZeroFlag && capacity == 0) {
    return '';
  }

  if (type.unlimitedCapacityFlag) {
    return i18n.t('roomBuilder.assigned', { assigned });
  }

  return i18n.t('roomBuilder.assignedOfCapacity', { assigned, capacity });
}

export function mapRoomTypeOptions(roomTypes, singleRoomFlag, studyFlag) {
  const typeOnlineOptions = [];
  const typePaperOptions = [];
  const filteredByStudyFlagRoomTypes = roomTypes.filter((type) => type.studyFlag === studyFlag);
  if (filteredByStudyFlagRoomTypes.length) {
    orderBy(filteredByStudyFlagRoomTypes, ['displayOrder'], ['asc']).forEach((type) => {
      if (type.onlineFlag) {
        typeOnlineOptions.push({
          label: type.name,
          key: `roomType-${type.id}`,
          value: type.id,
          name: 'roomType',
        });
      } else {
        typePaperOptions.push({
          label: type.name,
          key: `roomType-${type.id}`,
          value: type.id,
          disabled: type.name.includes(ROOM_TYPES.combination) ? singleRoomFlag : false,
          name: 'roomType',
        });
      }
    });
  }
  return {
    online: typeOnlineOptions,
    paper: typePaperOptions,
  };
}

export function str2ClassName(str) {
  return str.replace(/\s+/g, '-').toLowerCase();
}

export function getFirstElement(list) {
  const [first] = list;

  return first;
}

export function placePathParams(pathRegex, params) {
  let segments = pathRegex.split('/');
  return segments
    .map((segment) => {
      let offset = segment.indexOf(':') + 1;
      if (!offset) return segment;

      let key = segment.slice(offset);
      return params[key];
    })
    .join('/');
}

export function isWindowEvent(testingEvent) {
  return (
    !!testingEvent.endTestDate &&
    moment(testingEvent.endTestDate).isAfter(moment(testingEvent.testDate), 'day')
  );
}

export function isEventDate(testingEvent, serverDate) {
  if (isWindowEvent(testingEvent)) {
    return (
      moment(serverDate).isSameOrAfter(moment(testingEvent.testDate), 'day') &&
      moment(serverDate).isSameOrBefore(moment(testingEvent.endTestDate), 'day')
    );
  } else {
    return moment(serverDate).isSame(moment(testingEvent.testDate), 'day');
  }
}

export function getPersonnelRolesMapper(roles) {
  let data = {};
  roles.map((role) => {
    data[role.role] = role.role;
  });
  return data;
}

export function getPersonnelRoles() {
  const state = store.getState();

  return getPersonnelRolesMapper(state.statics.roles);
}

export function getPersonnelRolesName() {
  let data = {};
  const state = store.getState();

  state.statics.roles.map((role) => {
    data[role.role] = role.name;
  });
  return data;
}

export function getRoleById(roleId) {
  const state = store.getState();
  return state.statics.roles.find((role) => role.id === roleId);
}

export function isRoomOnline(roomType) {
  return !!(roomType && roomType.onlineFlag);
}

export function checkLocEvOnlineCapacity(locationEvent) {
  return (
    locationEvent &&
    locationEvent.locationEventCapacities.some(
      (locExam) => locExam.capacityType.onlineFlag && locExam.capacity
    )
  );
}

export function checkLocEvPaperCapacity(locationEvent) {
  return (
    locationEvent &&
    locationEvent.locationEventCapacities.some(
      (locExam) => !locExam.capacityType.onlineFlag && locExam.capacity
    )
  );
}

export function hasRoomMaterialsRequired(roomType) {
  return !!(roomType && roomType.trackMaterialFlag);
}

export function requirePersonalInformation(user) {
  return isEmpty(user) || (isEmpty(user.firstName) && isEmpty(user.lastName));
}

export function mapCapacityTypes(capacityTypes) {
  const result = [];
  capacityTypes.map((capacity) => (result[capacity.id] = capacity.name));
  return result;
}

export function checkForOnlineRooms(rooms) {
  return rooms.some((room) => room.roomType && room.roomType.onlineFlag);
}

export function mapTypeOptions(types, onlineFlag, editType, hasUndefinedType, studyFlag) {
  const typeOptions = [];
  if (types.length) {
    orderBy(
      types
        .filter((type) => Boolean(type.studyFlag) === Boolean(studyFlag))
        .filter((type) => Boolean(type.onlineFlag) === Boolean(onlineFlag)),
      ['displayOrder'],
      ['asc']
    ).forEach((option) => {
      typeOptions.push({
        label: option.name,
        key: `roomType-${option.id}`,
        value: option.id,
        name: 'roomType',
      });
    });
    if (!onlineFlag && !editType && hasUndefinedType) {
      typeOptions.push({
        label: i18n.t('roomBuilder.chooseLaterLabel'),
        key: 'roomType-',
        value: -1,
        name: 'roomType',
      });
    }
  }

  return typeOptions;
}

export function mapTimingOptions(roomTypes, mappedRoomTiming, value) {
  const selectedType = value ? roomTypes.find((item) => item.id === Number(value)) : null;
  const timingCodes = selectedType ? selectedType.timingCodes.split(',') : ['standard'];
  let timingOptions = [];

  timingCodes.forEach((item) => {
    timingOptions.push({
      key: item,
      label: mappedRoomTiming[item]?.name,
      value: item,
    });
  });
  return timingOptions;
}

export function applyActNatlTimingNames(options, programCode) {
  if (programCode !== 'actnatl') return options;
  return options.map((opt) => ({
    ...opt,
    label: ACT_NATIONAL_TIMING_NAMES[opt.key] || opt.label,
  }));
}

export function getRoomTimingName(timingCode, programCode, mappedRoomTiming) {
  if (!timingCode) return null;
  if (programCode === 'actnatl' && ACT_NATIONAL_TIMING_NAMES[timingCode]) {
    return ACT_NATIONAL_TIMING_NAMES[timingCode];
  }
  return mappedRoomTiming?.[timingCode]?.name || timingCode;
}
